import type { ComponentType, ReactNode } from 'react';
import type { AnswerMap, AnswerValue, Field } from '../types';

export interface FieldRendererProps {
  field: Field;
  value: AnswerValue;
  onChange: (value: AnswerValue) => void;
  readOnly?: boolean;
  /** All current answers in the form — needed by CalculatedField to read its source fields. */
  answers: AnswerMap;
}

export interface FieldEditorProps {
  field: Field;
  onChange: (patch: Partial<Field>) => void;
  /** Every field currently in the document — lets editors (e.g. CalculatedField) reference other fields by handle. */
  allFields: Field[];
}

export type FieldTypeCategory = 'input' | 'formatted' | 'choice' | 'layout' | 'advanced';

/**
 * Replaces the legacy `QuestionFormat` lookup table with an extensible, typed
 * plugin registry. Hosts add custom field types by passing extra
 * FieldTypeDefinition entries to <FormBuilder fieldTypes={...}> — no schema
 * migration required.
 */
export interface FieldTypeDefinition {
  key: string;
  label: string;
  category: FieldTypeCategory;
  /** Icon element shown in the palette. */
  icon: ReactNode;
  defaultProps: Partial<Field>;
  /** True for field types that render content but never collect an answer (e.g. Label). */
  isDisplayOnly?: boolean;
  Renderer: ComponentType<FieldRendererProps>;
  /** Optional type-specific settings tab in the Property Inspector (Options editor, Calc editor, ...). */
  PropertyEditor?: ComponentType<FieldEditorProps>;
}

export type FieldTypeRegistry = Record<string, FieldTypeDefinition>;

import { definition as labelDefinition } from './types/LabelField';
import { definition as textDefinition } from './types/TextField';
import { definition as textAreaDefinition } from './types/TextAreaField';
import { definition as numberDefinition } from './types/NumberField';
import { definition as dateDefinition } from './types/DateField';
import { definition as dateTimeDefinition } from './types/DateTimeField';
import { definition as checkboxDefinition } from './types/CheckboxField';
import { definition as radioDefinition } from './types/RadioField';
import { definition as dropdownDefinition } from './types/DropdownField';
import { definition as multiSelectDefinition } from './types/MultiSelectField';
import { definition as likertScaleDefinition } from './types/LikertScaleField';
import { definition as calculatedDefinition } from './types/CalculatedField';
import { definition as signatureDefinition } from './types/SignatureField';
import { definition as injectedDataDefinition } from './types/InjectedDataField';
import { definition as emailDefinition } from './types/EmailField';
import { definition as phoneDefinition } from './types/PhoneField';
import { definition as ssnDefinition } from './types/SsnField';
import { definition as zipCodeDefinition } from './types/ZipCodeField';

export const builtInFieldTypes: FieldTypeDefinition[] = [
  labelDefinition,
  textDefinition,
  textAreaDefinition,
  numberDefinition,
  dateDefinition,
  dateTimeDefinition,
  checkboxDefinition,
  radioDefinition,
  dropdownDefinition,
  multiSelectDefinition,
  likertScaleDefinition,
  calculatedDefinition,
  signatureDefinition,
  injectedDataDefinition,
  emailDefinition,
  phoneDefinition,
  ssnDefinition,
  zipCodeDefinition,
];

/** Merges host-supplied field types into the built-in set, by `key` (host entries win on collision). */
export function createFieldTypeRegistry(extra: FieldTypeDefinition[] = []): FieldTypeRegistry {
  const registry: FieldTypeRegistry = {};
  for (const def of builtInFieldTypes) registry[def.key] = def;
  for (const def of extra) registry[def.key] = def;
  return registry;
}
